import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/index";
import PatientLayout from "@/components/layout/PatientLayout";
import api from "@/api";
import { toast } from "sonner";
import { Calendar, Plus, CheckCircle, XCircle, Clock, MapPin, ThumbsUp, ThumbsDown, Home } from "lucide-react";

const STATUS_CONFIG = {
  PROPOSED: { color: "bg-blue-100 text-blue-700", icon: Clock },
  CONFIRMED: { color: "bg-green-100 text-green-700", icon: CheckCircle },
  DECLINED: { color: "bg-orange-100 text-orange-700", icon: ThumbsDown },
  CANCELLED: { color: "bg-red-100 text-red-700", icon: XCircle },
  COMPLETED: { color: "bg-slate-100 text-slate-600", icon: CheckCircle },
};

export default function PatientAppointments() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchAppointments();
  }, [user.id]);

  const fetchAppointments = async () => {
    try {
      const res = await api.get(`/appointments?patientId=${user.id}`);
      setAppointments(res.data);
    } catch {
      toast.error(t("appointments.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const requestAppointment = async () => {
    setSubmitting(true);
    try {
      await api.post("/appointments/requests", {
        patientId: user.id,
        type: "consultation",
        notes,
      });
      toast.success(t("appointments.requestSuccess"));
      setShowRequestForm(false);
      setNotes("");
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("appointments.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptAppointment = async (appointmentId) => {
    setActionLoading(appointmentId);
    try {
      await api.patch(`/appointments/${appointmentId}/confirm`, {});
      toast.success(t("appointments.accepted"));
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("appointments.error"));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeclineAppointment = async (appointmentId) => {
    setActionLoading(appointmentId);
    try {
      await api.patch(`/appointments/${appointmentId}/cancel`);
      toast.success(t("appointments.declined"));
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("appointments.error"));
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <PatientLayout>
      <div className="space-y-5">
        <div className="flex items-start justify-between gap-4 animate-fade-in-up">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("appointments.title")}</h1>
            <p className="text-slate-500 mt-1" data-testid="patient-appointments-subtitle">{t("appointments.subtitle")}</p>
          </div>
          <button
            onClick={() => setShowRequestForm(!showRequestForm)}
            data-testid="request-appointment-btn"
            className="flex items-center gap-1.5 bg-emerald-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t("appointments.request")}
          </button>
        </div>

        {/* Request Form */}
        {showRequestForm && (
          <div className="patient-card animate-fade-in-up border-emerald-200 bg-emerald-50">
            <h3 className="font-semibold text-slate-800 mb-3">{t("appointments.request")}</h3>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              data-testid="appointment-notes"
              placeholder={t("appointments.notesPlaceholder")}
              rows={3}
              className="w-full border border-slate-200 rounded-xl p-3 text-base bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200 resize-none"
            />
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setShowRequestForm(false)}
                className="flex-1 py-3 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-50"
              >
                {t("cancel")}
              </button>
              <button
                onClick={requestAppointment}
                disabled={submitting}
                data-testid="submit-appointment"
                className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                {submitting ? "..." : t("submit")}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-slate-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : appointments.length === 0 ? (
          <div className="text-center py-12 animate-fade-in-up">
            <Calendar className="w-14 h-14 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">{t("appointments.noAppointments")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {appointments.map((appt) => {
              const config = STATUS_CONFIG[appt.status] || STATUS_CONFIG.PROPOSED;
              const Icon = config.icon;
              const isProposed = appt.status === "PROPOSED";
              const isProposedByDoctor = isProposed && appt.requestedByRole !== "PATIENT";
              const isLoading = actionLoading === appt.id;
              
              return (
                <div
                  key={appt.id}
                  data-testid={`appointment-${appt.id}`}
                  className={`patient-card animate-fade-in-up ${isProposedByDoctor ? 'border-blue-300 bg-blue-50' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-xl ${config.color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        {appt.date ? (
                          <p className="font-semibold text-slate-800 text-base">
                            {new Date(appt.date).toLocaleDateString(language === "ar" ? "ar-MA" : "fr-FR", {
                              weekday: "long", day: "numeric", month: "long",
                              hour: "2-digit", minute: "2-digit"
                            })}
                          </p>
                        ) : (
                          <p className="font-semibold text-slate-600">{t("appointments.dateToConfirm")}</p>
                        )}
                        {appt.location && (
                          <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3 h-3" />
                            {appt.location}
                          </p>
                        )}
                        {appt.homeIntervention && (
                          <p
                            className="text-sm text-emerald-700 flex items-center gap-1 mt-0.5 font-medium"
                            data-testid={`patient-appointment-home-intervention-${appt.id}`}
                          >
                            <Home className="w-3 h-3" />
                            Prélèvement à domicile
                          </p>
                        )}
                        {appt.type && (
                          <p className="text-sm text-blue-600 mt-0.5 flex items-center gap-2">
                            <span className="capitalize">{appt.type.replace("_", " ")}</span>
                            {appt.source === "HANA_MANUAL" && (
                              <span
                                data-testid={`patient-declared-badge-${appt.id}`}
                                title={t("appointments.declarativeTooltip")}
                                className="text-[10px] font-semibold px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full"
                              >
                                {t("appointments.declarativeBadge")}
                              </span>
                            )}
                          </p>
                        )}
                        {appt.notes && (
                          <p className="text-sm text-slate-400 mt-1">{appt.notes}</p>
                        )}
                      </div>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${config.color}`}>
                      {t(`appointments.status.${appt.status}`)}
                    </span>
                  </div>
                  
                  {/* Action buttons for proposed appointments from doctor */}
                  {isProposedByDoctor && (
                    <div className="mt-4 pt-3 border-t border-blue-200">
                      <p className="text-sm text-blue-700 mb-3 font-medium">
                        {t("appointments.proposedByDoctor")}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAcceptAppointment(appt.id)}
                          disabled={isLoading}
                          data-testid={`accept-appointment-${appt.id}`}
                          className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                        >
                          <ThumbsUp className="w-4 h-4" />
                          {t("appointments.accept")}
                        </button>
                        <button
                          onClick={() => handleDeclineAppointment(appt.id)}
                          disabled={isLoading}
                          data-testid={`decline-appointment-${appt.id}`}
                          className="flex-1 flex items-center justify-center gap-2 py-3 bg-white border-2 border-orange-400 text-orange-600 rounded-xl font-medium hover:bg-orange-50 disabled:opacity-50 transition-colors"
                        >
                          <ThumbsDown className="w-4 h-4" />
                          {t("appointments.decline")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PatientLayout>
  );
}
